import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Info, ListChecks, Boxes, Camera, NotebookPen, PenLine, Flag, Wrench, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { StepInfo, StepChecklist, StepEquipment, StepPhotos, StepNotes, StepSignature, StepClose } from "@/components/tech/Steps";
import { ServiceList, SvcChecklist, SvcEquipment, SvcPhotos, SvcNotes, SvcFinish, MainDone } from "@/components/tech/ServiceSteps";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const STEPS = [
  { id: "info", label: "Info", icon: Info },
  { id: "checklist", label: "Checklist", icon: ListChecks },
  { id: "equipos", label: "Equipos", icon: Boxes },
  { id: "fotos", label: "Fotos", icon: Camera },
  { id: "notas", label: "Notas", icon: NotebookPen },
  { id: "firma", label: "Firma", icon: PenLine },
  { id: "cierre", label: "Cierre", icon: Flag },
];
const GPS_MID = [
  { id: "checklist_inicial", label: "Inicial", icon: ListChecks },
  { id: "equipos", label: "Equipos / Fotos", icon: Boxes },
  { id: "checklist_final", label: "Final", icon: ListChecks },
];
const DONE = { id: "terminar", label: "Terminar", icon: CheckCircle2 };
const stepsFor = (wo) => {
  if (wo?.business_unit_code !== "gps") return STEPS;
  const initial = (wo.checklist?.items || []).some((i) => i.phase === "inicial");
  return [STEPS[0], ...GPS_MID.filter((s) => initial || s.id !== "checklist_inicial"), ...STEPS.slice(4)];
};
const activeExtras = (wo) => (wo?.extra_services || []).filter((s) => !s.removed);
// Multiservicio: cada servicio tiene su propia secuencia; firma y cierre generales al final.
const flowFor = (wo) => {
  const base = stepsFor(wo);
  const extras = activeExtras(wo);
  if (!extras.length) return base;
  const svc = (list, n) => list.map((s) => ({ ...s, svc: n }));
  return [base[0], ...svc([...base.slice(1, -2), DONE], 0),
    ...extras.flatMap((_, k) => svc([...STEPS.slice(1, 5), DONE], k + 1)), ...base.slice(-2)];
};
const groupOf = (s) => (s.svc ?? s.id);
const STARTABLE = ["asignada", "programada", "pendiente", "nueva", "en_revision", "pendiente_programacion"];
const TITLES = { info: "Información", checklist: "Checklist", checklist_inicial: "Checklist inicial", checklist_final: "Checklist final", fotos: "Evidencia fotográfica", notas: "Observaciones", firma: "Firma de quien recibe", cierre: "Cierre del servicio", terminar: "Terminar servicio" };

const StepButton = ({ s, active, onClick, testid, label, Icon, done }) => (
  <button onClick={onClick} data-testid={testid}
    className={cn("flex min-w-[52px] flex-1 flex-col items-center gap-1 rounded-lg px-1 py-1.5 text-[10px] font-medium uppercase tracking-wide transition-colors",
      active ? "bg-primary/15 text-primary" : done ? "text-emerald-400" : "text-muted-foreground")}>
    <Icon className="h-4 w-4" />{label}
  </button>
);

const Stepper = ({ steps, step, setStep, multi, extras }) => {
  if (!multi) return (
    <nav className="flex overflow-x-auto px-2 pb-2" data-testid="tech-stepper">
      {steps.map((s, i) => <StepButton key={s.id} s={s} active={i === step} onClick={() => setStep(i)} testid={`tech-step-${s.id}`} label={s.label} Icon={s.icon} />)}
    </nav>
  );
  const groups = [...new Set(steps.map(groupOf))];
  const cur = groupOf(steps[step]);
  return (
    <>
      <nav className="flex overflow-x-auto px-2 pb-1" data-testid="tech-stepper">
        {groups.map((g) => {
          const first = steps.findIndex((s) => groupOf(s) === g);
          const isSvc = typeof g === "number";
          const done = isSvc && g > 0 && ["completada", "completada_obs"].includes(extras[g - 1]?.outcome?.result);
          return <StepButton key={g} active={g === cur} done={done} onClick={() => setStep(first)} testid={isSvc ? `tech-step-svc-${g + 1}` : `tech-step-${g}`}
            label={isSvc ? `Servicio ${g + 1}` : steps[first].label} Icon={isSvc ? Wrench : steps[first].icon} />;
        })}
      </nav>
      {typeof cur === "number" && (
        <nav className="flex overflow-x-auto border-t border-border/60 px-2 py-1" data-testid={`tech-substepper-svc-${cur + 1}`}>
          {steps.map((s, i) => (groupOf(s) === cur ? <StepButton key={`${cur}-${s.id}`} active={i === step} onClick={() => setStep(i)} testid={`tech-step-svc-${cur + 1}-${s.id}`} label={s.label} Icon={s.icon} /> : null))}
        </nav>
      )}
    </>
  );
};

export default function TechWorkOrder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [wo, setWo] = useState(null);
  const [step, setStep] = useState(0);
  const [starting, setStarting] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [mainPhotos, setMainPhotos] = useState(0);

  const load = useCallback(() => api.get(`/work-orders/${id}`).then((r) => setWo(r.data)).catch(() => nav("/tecnico")), [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/work-orders/${id}/evidence`).then((r) => { setPhotoCount(r.data.length); setMainPhotos(r.data.filter((e) => !e.service_id).length); });
  }, [id, step]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/work-orders/${id}/start`);
      setWo(data);
      toast.success("Servicio iniciado");
      setStep(1);
    } catch (e) { toast.error(errMsg(e)); } finally { setStarting(false); }
  };

  const finish = async (body) => {
    try {
      await api.post(`/work-orders/${id}/finish`, body);
      toast.success(body.result === "completada" ? "OS enviada a revisión" : "OS marcada como pendiente");
      nav("/tecnico");
    } catch (e) { toast.error(errMsg(e), { duration: 6000 }); }
  };

  if (!wo) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const editable = wo.status === "en_proceso";
  const props = { wo, setWo, editable };
  const extras = activeExtras(wo);
  const multi = extras.length > 0;
  const steps = flowFor(wo);
  const idx = Math.min(step, steps.length - 1);
  const cur = steps[idx];
  const current = cur.id;
  const sn = cur.svc ?? null;
  const extra = sn > 0 ? extras[sn - 1] : null;
  const next = () => setStep(Math.min(idx + 1, steps.length - 1));
  const sp = extra && { ...props, s: extra, n: sn + 1 };
  const svcName = sn === null ? null : sn === 0 ? `${wo.service_type_name} · ${wo.business_unit_name}` : `${extra.service_type_name} · ${extra.business_unit_name}`;
  const title = current === "equipos" ? (wo.business_unit_code === "gps" && sn === 0 ? "Equipos / Fotos" : "Equipamiento instalado") : TITLES[current];

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 pt-[max(env(safe-area-inset-top),0.5rem)] backdrop-blur-xl">
        <div className="flex items-center gap-2 px-2 py-2">
          <Button variant="ghost" size="icon" onClick={() => nav("/tecnico")} data-testid="tech-back-button"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm font-bold text-primary" data-testid="tech-wo-folio">{wo.folio}</div>
            <div className="truncate text-xs text-muted-foreground">{wo.client_name}</div>
          </div>
          <StatusBadge status={wo.status} />
        </div>
        <Stepper steps={steps} step={idx} setStep={setStep} multi={multi} extras={extras} />
      </header>
      {!editable && wo.status !== "pendiente_cierre" && idx > 0 && (
        <div className="mx-4 mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="tech-not-started-banner">Inicia el servicio en la pestaña Info para registrar información.</div>
      )}
      {editable && wo.return_history?.length > 0 && (
        <div className="mx-4 mt-4 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-sm text-orange-200" data-testid="tech-returned-banner">
          Devuelta por {wo.return_history.at(-1).by_name} para corrección: {wo.return_history.at(-1).reason}. Corrige y vuelve a enviarla desde Cierre.
        </div>
      )}
      {wo.status === "pendiente_cierre" && <div className="mx-4 mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">Enviada a revisión administrativa. Puedes agregar fotos y comentarios complementarios.</div>}
      <main key={`${sn}-${current}`} className="fade-up flex-1 px-4 py-4 pb-28">
        {multi && sn !== null && <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.2em] text-primary" data-testid="tech-current-service">Servicio {sn + 1} · {svcName}</div>}
        <h2 className="mb-4 font-display text-2xl font-bold">{title}</h2>
        {current === "info" && <StepInfo wo={wo} setWo={setWo} canStart={STARTABLE.includes(wo.status)} onStart={start} starting={starting} />}
        {current === "info" && multi && <div className="mt-3"><ServiceList wo={wo} extras={extras} testid="tech-info-services" /></div>}
        {extra ? (
          <>
            {current === "checklist" && <SvcChecklist {...sp} />}
            {current === "equipos" && <SvcEquipment key={extra.id} {...sp} />}
            {current === "fotos" && <SvcPhotos {...sp} />}
            {current === "notas" && <SvcNotes key={extra.id} {...sp} />}
            {current === "terminar" && <SvcFinish key={extra.id} {...sp} onNext={next} />}
          </>
        ) : (
          <>
            {current === "checklist" && <StepChecklist {...props} />}
            {current === "checklist_inicial" && <StepChecklist {...props} phase="inicial" />}
            {current === "checklist_final" && <StepChecklist {...props} phase="final" />}
            {current === "equipos" && <StepEquipment {...props} />}
            {current === "fotos" && <StepPhotos {...props} />}
            {current === "notas" && <StepNotes {...props} />}
            {current === "terminar" && <MainDone wo={wo} photos={mainPhotos} onNext={next} />}
          </>
        )}
        {current === "firma" && <StepSignature {...props} />}
        {current === "cierre" && multi && <div className="mb-4"><ServiceList wo={wo} extras={extras} testid="tech-close-services" /></div>}
        {current === "cierre" && <StepClose {...props} onFinish={finish} photoCount={photoCount} />}
      </main>
      <footer className="safe-bottom fixed inset-x-0 bottom-0 z-30 mx-auto grid max-w-md grid-cols-2 gap-2 border-t border-border bg-background/95 px-4 pt-3 backdrop-blur-xl">
        <Button variant="outline" className="h-12" disabled={idx === 0} onClick={() => setStep(idx - 1)} data-testid="tech-prev-step-button"><ChevronLeft className="mr-1 h-4 w-4" />Anterior</Button>
        <Button className="h-12" disabled={idx === steps.length - 1} onClick={next} data-testid="tech-next-step-button">Siguiente<ChevronRight className="ml-1 h-4 w-4" /></Button>
      </footer>
    </div>
  );
}
